import { HttpErrorResponse } from '@angular/common/http';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	DestroyRef,
	inject,
	input,
	OnInit,
	signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { AlertService } from '@marmon/frontend/alert';
import { LoggerService } from '@marmon/frontend/logging';
import {
	DriverReportTargetIds,
	Insight,
	InsightRunProgress,
	insightNarrativeText,
	InsightResponseMeta,
	insightResponseMeta,
	isCompleteInsight,
	isPollableInsight,
} from '@marmon/shared/types/krista';
import { TuiButton, TuiIcon } from '@taiga-ui/core';
import { TuiSkeleton } from '@taiga-ui/kit';
import { combineLatest, distinctUntilChanged, map, Subscription } from 'rxjs';
import {
	AnalysisStateService,
	DriverReportInsightService,
} from '../../services';
import { InsightPanelComponent } from '../insight-panel/insight-panel.component';
import { InsightProgressComponent } from '../insight-progress/insight-progress.component';

export type DriverReportPhase =
	| 'loading'
	| 'ready'
	| 'generating'
	| 'view'
	| 'error';

let nextDriverReportId = 0;

/**
 * Analysis Driver Report. On view, the card reads the insight cache.
 * A pending Krista event is polled again. A finished report offers a
 * view action that opens the shared insight panel for this tab.
 */
@Component({
	standalone: true,
	selector: 'mar-driver-report',
	imports: [
		TranslocoPipe,
		TuiButton,
		TuiIcon,
		TuiSkeleton,
		InsightPanelComponent,
		InsightProgressComponent,
	],
	templateUrl: 'driver-report.component.html',
	styleUrl: 'driver-report.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: { class: 'block w-full min-w-0' },
})
export class DriverReportComponent implements OnInit {
	private readonly transloco = inject(TranslocoService);
	private readonly destroyRef = inject(DestroyRef);
	private readonly state = inject(AnalysisStateService);
	private readonly insights = inject(DriverReportInsightService);
	private readonly logger = inject(LoggerService);
	private readonly alerts = inject(AlertService);
	private readonly instanceId = ++nextDriverReportId;
	private generation: Subscription | null = null;
	private trackedKey = '';

	/** Insight target for this tab. Cache, Krista, and the panel all key off it. */
	readonly targetId = input.required<string>();

	private readonly analysis = toSignal(this.state.analysis$, {
		initialValue: this.state.analysis,
	});

	private readonly calculation = toSignal(this.state.calculation$, {
		initialValue: this.state.calculation,
	});

	readonly titleId = `driver-report-title-${this.instanceId}`;

	readonly phase = signal<DriverReportPhase>('loading');
	readonly narrative = signal('');
	readonly progress = signal<InsightRunProgress | null>(null);
	readonly meta = signal<InsightResponseMeta | null>(null);
	readonly panelOpen = signal(false);
	readonly contextKey = computed(() =>
		driverReportContextKey(this.targetId()),
	);

	readonly canGenerate = computed(
		() => !!this.analysis()?.id && !!this.calculation()?.id,
	);

	readonly progressPercent = computed(() =>
		Math.round((this.progress()?.value ?? 0) * 100),
	);

	readonly progressMessage = computed(
		() => this.progress()?.message?.trim() ?? '',
	);

	constructor() {
		this.destroyRef.onDestroy(() => this.generation?.unsubscribe());
	}

	ngOnInit() {
		combineLatest([this.state.analysis$, this.state.calculation$])
			.pipe(
				map(
					([analysis, calculation]) =>
						`${analysis?.id ?? ''}:${calculation?.id ?? ''}:${this.targetId()}`,
				),
				distinctUntilChanged(),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((key) => {
				if (key === this.trackedKey) return;
				this.trackedKey = key;
				this.restore();
			});
	}

	onGenerate() {
		if (this.phase() === 'generating' || this.phase() === 'loading') {
			return;
		}

		const analysisId = this.analysis()?.id;
		const calculationId = this.calculation()?.id;
		if (!analysisId || !calculationId) return;

		this.generation?.unsubscribe();
		this.narrative.set('');
		this.meta.set(null);
		this.progress.set(null);
		this.panelOpen.set(false);
		this.phase.set('generating');
		this.generation = this.insights
			.generate(analysisId, calculationId, this.targetId())
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (insight) => this.onInsight(insight),
				error: (error: unknown) => this.onRequestError(error),
				complete: () => {
					if (this.phase() === 'generating') this.showFailure(null);
				},
			});
	}

	onView() {
		if (!this.narrative()) return;
		this.panelOpen.set(true);
	}

	onClosePanel() {
		this.panelOpen.set(false);
	}

	private onInsight(insight: Insight, notify = true) {
		if (isPollableInsight(insight)) {
			this.progress.set(insight.progress ?? null);
			this.phase.set('generating');
			return;
		}

		if (insight.status === 'failed') {
			this.showFailure(insight.error?.message ?? null, notify);
			return;
		}

		const text = isCompleteInsight(insight)
			? insightNarrativeText(insight.response)
			: '';
		if (!text) {
			this.showFailure(null, notify);
			return;
		}

		this.narrative.set(text);
		this.meta.set(insightResponseMeta(insight.response));
		this.phase.set('view');
	}

	private onRequestError(error: unknown) {
		const detail = httpErrorMessage(error);
		this.logger.captureException(
			detail
				? new Error(detail)
				: error instanceof Error
					? error
					: new Error('Driver report request failed'),
			{ source: 'DriverReportComponent.generate' },
		);
		this.alerts.open('generic-error').subscribe();
		this.presentFailure();
	}

	private showFailure(message: string | null, notify = true) {
		const text = this.failureText(message);
		if (notify) {
			this.logger.captureException(new Error(text), {
				source: 'DriverReportComponent.generate',
			});
			this.alerts.open('generic-error').subscribe();
		}
		this.presentFailure();
	}

	private presentFailure() {
		this.narrative.set('');
		this.meta.set(null);
		this.progress.set(null);
		this.panelOpen.set(false);
		this.phase.set('error');
	}

	private failureText(message: string | null): string {
		const trimmed = message?.trim();
		if (trimmed) return trimmed;
		return this.transloco.translate('analysis.driver-report.error') ?? '';
	}

	private restore() {
		this.generation?.unsubscribe();
		this.generation = null;
		this.narrative.set('');
		this.meta.set(null);
		this.progress.set(null);
		this.panelOpen.set(false);

		const analysisId = this.analysis()?.id;
		const calculationId = this.calculation()?.id;
		if (!analysisId || !calculationId) {
			this.phase.set('ready');
			return;
		}

		this.phase.set('loading');
		this.generation = this.insights
			.load(analysisId, calculationId, this.targetId())
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (insight) => {
					if (!insight) {
						this.phase.set('ready');
						return;
					}
					this.onInsight(insight, false);
				},
				error: (error: unknown) => this.onRequestError(error),
			});
	}
}

export function driverReportContextKey(targetId: string): string {
	switch (targetId) {
		case DriverReportTargetIds.profit:
			return 'analysis.driver-report.context.profit';
		case DriverReportTargetIds.customerProduct:
			return 'analysis.driver-report.context.customer-product';
		default:
			return 'analysis.driver-report.context.quad';
	}
}

function httpErrorMessage(error: unknown): string | null {
	if (!(error instanceof HttpErrorResponse)) return null;

	const body = error.error;
	if (typeof body === 'string' && body.trim()) return body.trim();
	if (!body || typeof body !== 'object' || !('message' in body)) return null;

	const message = (body as { message: unknown }).message;
	return typeof message === 'string' && message.trim()
		? message.trim()
		: null;
}
